<template>
  <div class="upload-request-form">
    <header class="request-purpose">
      <div class="purpose-icon" aria-hidden="true">
        <i class="fas fa-envelope-open-text"></i>
      </div>
      <div class="purpose-body">
        <h3>
          {{ subjectContext ? 'Request an updated certificate' : 'Request a certificate upload' }}
        </h3>
        <p>
          Ask the agent of record to upload a COI or policy documents
          <template v-if="subjectContext">
            for <strong>{{ subjectContext.subject_entity_name }}</strong>
          </template>
          so they can be reviewed against this project’s insurance requirements.
        </p>
      </div>
    </header>

    <form @submit.prevent="sendRequest" class="request-form">
      <!-- Recipients Section -->
      <div class="form-group">
        <div class="recipients-header">
          <label>Recipients *</label>
          <span class="recipients-count" :class="{ 'has-recipients': recipients.length > 0 }">
            {{ recipients.length }} selected
          </span>
        </div>

        <!-- Loading contacts -->
        <div v-if="loadingContacts" class="contacts-loading">
          <i class="fas fa-spinner fa-spin"></i> Loading contacts for {{ subjectContext?.subject_entity_name }}...
        </div>

        <!-- Available contacts to add (shown when we have contacts and not all are selected) -->
        <div v-if="!loadingContacts && availableContacts.length > 0" class="available-contacts">
          <div class="available-contacts-label">
            <i class="fas fa-address-book"></i> Contacts for {{ subjectContext?.subject_entity_name }}
          </div>
          <div class="contact-chips-list">
            <button
              v-for="contact in availableContacts"
              :key="contact.id"
              type="button"
              class="contact-chip-btn"
              :class="{ 'is-added': isRecipientAdded(contact.email) }"
              @click="toggleContact(contact)"
            >
              <span class="chip-avatar">{{ contactInitials(contact.name) }}</span>
              <span class="chip-info">
                <span class="chip-name">{{ contact.name }}</span>
                <span class="chip-role">{{ formatContactRole(contact.contact_role) }}</span>
              </span>
              <span class="chip-action">
                <i :class="isRecipientAdded(contact.email) ? 'fas fa-check' : 'fas fa-plus'"></i>
              </span>
            </button>
          </div>
        </div>

        <!-- No contacts found hint -->
        <div v-else-if="!loadingContacts && contactsFetched && subjectContext && subjectContacts.length === 0" class="no-contacts-hint">
          <i class="fas fa-info-circle"></i>
          No saved contacts found for <strong>{{ subjectContext.subject_entity_name }}</strong> — add emails below.
        </div>

        <!-- Selected recipients chips -->
        <div v-if="recipients.length > 0" class="selected-recipients">
          <div class="selected-label">Will receive this request:</div>
          <div class="selected-chips">
            <div v-for="(r, idx) in recipients" :key="idx" class="selected-chip">
              <span class="chip-avatar chip-avatar-sm">{{ contactInitials(r.name || r.email) }}</span>
              <span class="selected-chip-text">
                <span class="selected-name">{{ r.name || r.email }}</span>
                <span v-if="r.name" class="selected-email">{{ r.email }}</span>
              </span>
              <button type="button" class="chip-remove" @click="removeRecipient(idx)" title="Remove">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Add email input row -->
        <div class="add-email-row">
          <input
            v-model="newEmailInput"
            type="email"
            class="form-control"
            placeholder="Add email address..."
            @keydown.enter.prevent="addManualEmail"
            @keydown="$event.key === ',' && ($event.preventDefault(), addManualEmail())"
          >
          <button
            type="button"
            class="btn-add-email"
            :disabled="!newEmailInput.trim()"
            @click="addManualEmail"
          >
            <i class="fas fa-plus"></i> Add
          </button>
        </div>
        <div v-if="recipientError" class="recipient-error small mt-1">
          <i class="fas fa-exclamation-circle"></i> {{ recipientError }}
        </div>
      </div>
      

      <!-- Analysis Type Selection -->
      <div class="form-group">
        <label for="workflow-select">Analysis to Trigger Upon Upload</label>
        <select id="workflow-select" v-model="analysisType" :required="!skipProcessing" class="form-control" :disabled="skipProcessing">
          <option value="">-- Select Analysis Type --</option>
          <option value="COMPREHENSIVE">
            🛡️ Comprehensive Check {{ subjectContext ? '(Recommended for Subjects)' : '(Advanced Analysis)' }}
          </option> 
          <option v-for="workflow in workflows" 
                  :key="workflow.id" 
                  :value="workflow.id">
             Workflow: {{ workflow.name }}
          </option>
        </select>
        <div class="form-help mt-1">
          <div v-if="skipProcessing" class="text-warning small">
            <i class="fas fa-forward"></i> 
            No analysis will be triggered - document will be saved for reference only.
          </div>
          <div v-else-if="analysisType === 'COMPREHENSIVE'" class="text-success small">
            <i class="fas fa-check-circle"></i> 
            Will run comprehensive compliance checks using the project's configured definition.
          </div>
          <div v-else class="text-muted small">
            Select 'Comprehensive Check' for complete analysis, or choose a specific workflow for targeted processing.
          </div>
        </div>
      </div>
      
      <!-- Skip Processing Option -->
      <div class="skip-processing-group">
        <label class="checkbox-label skip-checkbox">
          <input type="checkbox" v-model="skipProcessing" @change="handleSkipProcessingChange">
          <span><i class="fas fa-forward"></i> Request for reference only (skip analysis)</span>
        </label>
        <div v-if="skipProcessing" class="skip-info">
          <i class="fas fa-info-circle"></i>
          Documents uploaded via this link will be saved but <strong>not analyzed</strong>. Use this for:
          <ul>
            <li>Sample documents for training</li>
            <li>Reference materials</li>
            <li>Documents to be analyzed later</li>
          </ul>
        </div>
      </div>
      
      <!-- Read-only mirror of ProjectUploadRequestConfig.
           PR B moved the editor to ProjectSettings.vue so the modal is
           now purely a send action — admins maintain certificate-holder
           text, project contact, and template override in one place. -->
      <div class="notif-config-section">
        <div class="notif-config-header notif-config-header--readonly">
          <div class="notif-config-title">
            <i class="fas fa-building"></i>
            <span>Certificate Holder &amp; Contact Info</span>
            <span v-if="notifConfigSaved" class="config-saved-badge">
              <i class="fas fa-check-circle"></i> Configured
            </span>
            <span v-else class="config-saved-badge config-saved-badge--missing">
              <i class="fas fa-exclamation-triangle"></i> Not configured
            </span>
          </div>
          <router-link
            v-if="projectId"
            :to="`/projects/${projectId}/settings`"
            class="notif-edit-link"
          >
            <i class="fas fa-pen"></i> Edit in Project Settings
          </router-link>
        </div>

        <div class="notif-config-body notif-config-body--readonly">
          <p v-if="!notifConfigSaved" class="notif-config-desc">
            <i class="fas fa-info-circle"></i>
            No certificate holder is configured for this project yet. The email body will
            still send, but the
            <code>CERTIFICATE HOLDER:</code> block will be omitted.
            <router-link :to="`/projects/${projectId}/settings`" class="notif-edit-inline">
              Configure it here →
            </router-link>
          </p>

          <div v-else class="notif-readonly-grid">
            <div v-if="notifConfig.certificate_holder_name" class="notif-readonly-row">
              <div class="notif-readonly-label">Certificate Holder</div>
              <div class="notif-readonly-value">
                {{ notifConfig.certificate_holder_name }}
                <div
                  v-if="notifConfig.certificate_holder_address"
                  class="notif-readonly-sub"
                >
                  {{ notifConfig.certificate_holder_address }}
                </div>
              </div>
            </div>
            <div
              v-if="notifConfig.contact_name || notifConfig.contact_email"
              class="notif-readonly-row"
            >
              <div class="notif-readonly-label">Project Contact</div>
              <div class="notif-readonly-value">
                <span v-if="notifConfig.contact_name">{{ notifConfig.contact_name }}</span>
                <span v-if="notifConfig.contact_title">, {{ notifConfig.contact_title }}</span>
                <span v-if="notifConfig.contact_email"> &mdash; {{ notifConfig.contact_email }}</span>
              </div>
            </div>
            <div v-if="notifConfig.special_instructions" class="notif-readonly-row">
              <div class="notif-readonly-label">Special Instructions</div>
              <div class="notif-readonly-value notif-readonly-instructions">
                {{ notifConfig.special_instructions }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CC Options -->
      <div class="cc-section">
        <div class="cc-section-title">
          <i class="fas fa-copy"></i>
          <span>Copy (CC) Options</span>
        </div>

        <!-- CC subcontractor toggle (only shown when we have a subject context) -->
        <div v-if="subjectContext && subjectContext.subject_entity" class="cc-option">
          <label class="checkbox-label cc-checkbox">
            <input type="checkbox" v-model="ccSubcontractor">
            <span>
              <i class="fas fa-building"></i>
              CC <strong>{{ subjectContext.subject_entity_name || 'Subcontractor' }}</strong> for awareness
              <span v-if="subjectContext.entity_primary_email" class="cc-email-hint">
                ({{ subjectContext.entity_primary_email }})
              </span>
              <span v-else class="cc-email-hint text-muted">(no email on file — can enter below)</span>
            </span>
          </label>
          <p class="cc-help">
            The subcontractor will receive a copy of this email. Useful when they may have
            documents on hand or need to be aware the request was sent to their agent.
          </p>
        </div>

        <!-- Additional CC addresses (free-entry) -->
        <div class="cc-additional">
          <label class="small fw-semibold">
            Additional CC addresses
            <span class="text-muted fw-normal">(optional — comma-separated or press Enter)</span>
          </label>
          <div class="cc-chips" v-if="additionalCcList.length">
            <span v-for="(addr, idx) in additionalCcList" :key="idx" class="cc-chip">
              {{ addr }}
              <button type="button" @click="removeAdditionalCc(idx)" class="cc-chip-remove">
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
          <div class="add-email-row mt-1">
            <input
              v-model="additionalCcInput"
              type="email"
              class="form-control"
              placeholder="cc@example.com"
              @keydown.enter.prevent="addAdditionalCc"
            >
            <button
              type="button"
              class="btn-add-email"
              :disabled="!additionalCcInput.trim()"
              @click="addAdditionalCc"
            >
              <i class="fas fa-plus"></i> Add
            </button>
          </div>
          <div v-if="ccInputError" class="recipient-error small mt-1">
            <i class="fas fa-exclamation-circle"></i> {{ ccInputError }}
          </div>
        </div>
      </div>

      <!-- AI Email Drafting -->
      <div class="ai-draft-section">
        <div class="ai-draft-header">
          <div class="ai-draft-title">
            <i class="fas fa-magic"></i>
            <span>Email Message</span>
          </div>
          <button
            type="button"
            class="btn-ai-draft"
            :disabled="generatingDraft || recipients.length === 0"
            @click="generateAIDraft"
          >
            <span v-if="generatingDraft" class="spinner-sm me-1"></span>
            <i v-else class="fas fa-wand-magic-sparkles me-1"></i>
            {{ generatingDraft ? 'Drafting...' : (aiDraftGenerated ? 'Re-draft with AI' : 'Draft with AI') }}
          </button>
        </div>

        <!-- AI Draft Options (shown while generating or after generation) -->
        <div v-if="showAIOptions" class="ai-options-row">
          <div class="ai-option-group">
            <label>Tone</label>
            <select v-model="aiTone" class="form-control form-control-sm">
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="formal">Formal</option>
              <option value="firm">Firm</option>
              <option value="empathetic">Empathetic</option>
            </select>
          </div>
          <div class="ai-option-group">
            <label>Urgency</label>
            <select v-model="aiUrgency" class="form-control form-control-sm">
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="low">Low</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <!-- AI Draft Preview (editable) -->
        <div v-if="aiDraftGenerated" class="ai-preview-block">
          <div class="ai-preview-badge">
            <i class="fas fa-sparkles"></i> AI-drafted — review and edit before sending
          </div>
          <div class="form-group mb-2">
            <label class="small fw-semibold">Subject</label>
            <input
              v-model="aiSubject"
              type="text"
              class="form-control form-control-sm"
            >
          </div>
          <div class="form-group">
            <label class="small fw-semibold">Message Body</label>
            <textarea
              v-model="aiBody"
              rows="8"
              class="form-control ai-body-textarea"
              placeholder="AI-generated message will appear here..."
            ></textarea>
          </div>
          <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="clearAIDraft">
            <i class="fas fa-times me-1"></i> Clear AI draft &amp; write manually
          </button>
        </div>

        <!-- Manual fallback message field (shown when no AI draft) -->
        <div v-else>
          <textarea 
            id="message"
            v-model="message" 
            placeholder="Add a custom message to include with the upload request email, or use 'Draft with AI' above to generate a personalized message."
            rows="3"
            class="form-control"
          ></textarea>
        </div>

        <div v-if="aiDraftError" class="alert alert-warning mt-2 small">
          <i class="fas fa-exclamation-triangle"></i> {{ aiDraftError }}
          <br><span class="text-muted">You can still send using the manual message field above.</span>
        </div>
      </div>
      
      <!-- Submit Section -->
      <div class="form-actions">
        <button 
          type="submit" 
          class="submit-btn" 
          :disabled="!canSubmit"
        >
          <span v-if="loading" class="spinner-sm me-1"></span>
          <i v-else class="fas fa-paper-plane me-1"></i>
          {{ loading ? 'Sending...' : recipients.length > 1 ? `Send to ${recipients.length} agents` : 'Send request' }}
        </button>
        
        <!-- Submit status help -->
        <div v-if="!canSubmit && !loading" class="mt-2 text-muted small">
          <i class="fas fa-info-circle"></i>
          Please {{ recipients.length === 0 ? 'add at least one recipient' : 'select an analysis type' }} to continue.
        </div>
      </div>
    </form>
    
    <!-- Status Messages -->
    <div v-if="success" class="success-message">
      <i class="fas fa-check-circle me-2"></i>
      Request sent successfully!
    </div>
    
    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>
    
    <!-- Add Contact Suggestion Modal -->
    <div v-if="showContactSuggestion" class="contact-suggestion mt-3 p-3 border rounded bg-info bg-opacity-10">
      <h6><i class="fas fa-lightbulb"></i> Contact Management Tip</h6>
      <p class="mb-2">To avoid manually entering emails in the future, you can add contacts for this entity:</p>
      <ol class="small">
        <li>Go to "Manage Entities" in the dashboard</li>
        <li>Find "{{ subjectContext?.subject_entity_name }}" and expand it</li>
        <li>Add contact details for future use</li>
      </ol>
      <button type="button" @click="showContactSuggestion = false" class="btn btn-sm btn-outline-secondary">
        <i class="fas fa-times"></i> Got it
      </button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'UploadRequestForm',
  props: {
    projectId: {
      type: [Number, String],
      required: true
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    workflows: {
      type: Array,
      default: () => []
    },
    subjectContext: {
        type: Object, 
        default: null
    }
  },
  data() {
    return {
      // Multi-recipient state
      recipients: [],        // [{ email, name, contact_role }]
      newEmailInput: '',
      recipientError: null,
      // Contact loading
      subjectContacts: [],
      loadingContacts: false,
      contactsFetched: false,
      contactError: null,
      // Form fields
      message: '',
      analysisType: '',
      loading: false,
      success: false,
      error: null,
      showContactSuggestion: false,
      skipProcessing: false,
      // AI email drafting
      generatingDraft: false,
      aiDraftGenerated: false,
      showAIOptions: false,
      aiTone: 'professional',
      aiUrgency: 'normal',
      aiSubject: '',
      aiBody: '',
      aiDraftError: null,
      // CC options
      ccSubcontractor: false,
      additionalCcList: [],
      additionalCcInput: '',
      ccInputError: null,
      // Read-only mirror of ProjectUploadRequestConfig — the editor now
      // lives in ProjectSettings.vue (PR B). We still load it here so the
      // user can see what will appear in the email before they hit Send.
      notifConfig: {
        certificate_holder_name: '',
        certificate_holder_address: '',
        contact_name: '',
        contact_title: '',
        contact_email: '',
        special_instructions: '',
      },
      notifConfigSaved: false,
    }
  },
  computed: {
    canSubmit() {
      return !this.loading &&
             this.recipients.length > 0 &&
             (this.analysisType || this.skipProcessing) &&
             !this.loadingContacts;
    },
    // contacts not yet added to recipients list
    availableContacts() {
      return this.subjectContacts.filter(c => !this.isRecipientAdded(c.email));
    },
  },
  watch: {
    subjectContext: {
      immediate: true,
      handler(newSubject) {
        if (newSubject && newSubject.subject_entity) {
          this.fetchSubjectContacts(newSubject.subject_entity);
        } else {
          this.resetContactState();
        }
      }
    }
  },
  mounted() {
    this.loadNotifConfig();
  },
  methods: {
    // ── Recipients ──────────────────────────────────────────────────────────

    isRecipientAdded(email) {
      return this.recipients.some(r => r.email.toLowerCase() === email.toLowerCase());
    },

    toggleContact(contact) {
      if (this.isRecipientAdded(contact.email)) {
        this.recipients = this.recipients.filter(
          r => r.email.toLowerCase() !== contact.email.toLowerCase()
        );
      } else {
        this.recipients.push({
          email: contact.email,
          name: contact.name,
          contact_role: contact.contact_role,
        });
      }
    },

    addManualEmail() {
      const addr = this.newEmailInput.trim();
      if (!addr) return;
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(addr)) {
        this.recipientError = `"${addr}" is not a valid email address.`;
        return;
      }
      if (this.isRecipientAdded(addr)) {
        this.recipientError = `${addr} is already in the list.`;
        return;
      }
      this.recipientError = null;
      this.recipients.push({ email: addr, name: '', contact_role: '' });
      this.newEmailInput = '';
    },

    removeRecipient(idx) {
      this.recipients.splice(idx, 1);
    },

    contactInitials(nameOrEmail) {
      if (!nameOrEmail) return '?';
      const parts = nameOrEmail.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return nameOrEmail[0].toUpperCase();
    },

    formatContactRole(role) {
      if (!role) return '';
      const map = {
        OWNER: 'Owner',
        GENERAL_MANAGER: 'General Manager',
        OPERATIONS_MANAGER: 'Operations Manager',
        INSURANCE_CONTACT: 'Insurance Contact',
        BILLING_CONTACT: 'Billing Contact',
        INSURANCE_AGENT: 'Insurance Agent',
        ACCOUNT_MANAGER: 'Account Manager',
        CLAIMS_HANDLER: 'Claims Handler',
        PRIMARY_CONTACT: 'Primary Contact',
        OTHER: 'Contact',
      };
      return map[role] || role;
    },

    // ── Contacts loading ─────────────────────────────────────────────────────

    resetContactState() {
      this.subjectContacts = [];
      this.contactsFetched = false;
      this.contactError = null;
      this.showContactSuggestion = false;
    },

    async fetchSubjectContacts(entityId) {
      this.loadingContacts = true;
      this.contactError = null;
      this.contactsFetched = false;
      this.subjectContacts = [];

      try {
        const response = await api.get('/contacts/', {
          params: { entity: entityId },
          timeout: 10000,
        });

        let contactsData = [];
        if (response?.data) {
          if (Array.isArray(response.data)) contactsData = response.data;
          else if (Array.isArray(response.data.results)) contactsData = response.data.results;
          else if (Array.isArray(response.data.data)) contactsData = response.data.data;
        }

        this.subjectContacts = contactsData.filter(c => c?.email && c?.name);
        this.contactsFetched = true;
      } catch (err) {
        this.contactError = this.getErrorMessage(err);
        this.contactsFetched = true;
      } finally {
        this.loadingContacts = false;
      }
    },

    getErrorMessage(error) {
      if (error.response) {
        if (error.response.status === 404) return 'Contact service not found.';
        if (error.response.status === 403) return 'Access denied to contact information.';
        if (error.response.data?.detail) return error.response.data.detail;
        if (error.response.data?.error) return error.response.data.error;
      }
      return error.message || 'Failed to load contacts';
    },

    showAddContactSuggestion() {
      this.showContactSuggestion = true;
    },

    handleSkipProcessingChange() {
      // When skip processing is enabled, clear the analysis type selection
      if (this.skipProcessing) {
        this.analysisType = '';
      }
    },

    async generateAIDraft() {
      this.showAIOptions = true;
      this.generatingDraft = true;
      this.aiDraftError = null;

      try {
        const response = await api.post(
          `/projects/${this.projectId}/preview-upload-request-email/`,
          {
            entity_name: this.subjectContext?.subject_entity_name ||
              (this.recipients[0]?.name || this.recipients[0]?.email || ''),
            analysis_type: this.analysisType || '',
            tone: this.aiTone,
            urgency: this.aiUrgency,
          }
        );

        if (response.data?.success) {
          this.aiSubject = response.data.subject || '';
          this.aiBody = (response.data.body_text || '').replace(/\[UPLOAD_LINK\]/g,
            '[secure upload link will be included in the sent email]');
          this.aiDraftGenerated = true;
        } else {
          this.aiDraftError = response.data?.error || 'Failed to generate draft.';
        }
      } catch (err) {
        this.aiDraftError = this.getErrorMessage(err) || 'Failed to generate AI draft.';
      } finally {
        this.generatingDraft = false;
      }
    },

    clearAIDraft() {
      this.aiDraftGenerated = false;
      this.aiSubject = '';
      this.aiBody = '';
      this.aiDraftError = null;
      this.showAIOptions = false;
    },

    async sendRequest() {
      console.log('🚀 Sending upload request...');
      
      this.loading = true;
      this.error = null;
      this.success = false;
      
      try {
        const emailsList = this.recipients.map(r => r.email);
        const payload = {
          emails: emailsList,
          // Keep 'email' for backward-compat with older backend paths
          email: emailsList[0] || undefined,
          message: this.message.trim() || undefined,
          // Pass AI-generated email if available
          ...(this.aiDraftGenerated && this.aiSubject ? { ai_subject: this.aiSubject } : {}),
          ...(this.aiDraftGenerated && this.aiBody ? { ai_body: this.aiBody } : {}),
          // CC options
          cc_subcontractor: this.ccSubcontractor,
          ...(this.additionalCcList.length ? { cc_emails: this.additionalCcList } : {}),
        };
        
        let apiUrl;
        let requestPayload = { ...payload };

        // Determine API endpoint and payload based on analysis type OR skip processing
        if (this.skipProcessing) {
          // Skip processing mode - use comprehensive endpoint with skip_processing flag
          console.log('⏭️ Skip processing mode - documents will be uploaded without analysis');
          apiUrl = `/projects/${this.projectId}/request-comprehensive-upload/`;
          
          // Add skip processing flag
          requestPayload.skip_processing = true;
          requestPayload.requested_role = 'VENDOR_CONTACT';
          
          if (this.subjectContext && this.subjectContext.subject_entity) {
            requestPayload.subject_entity_id = this.subjectContext.subject_entity;
          } else {
            throw new Error('Skip processing upload requests require a specific subject context.');
          }
          
        } else if (this.analysisType === 'COMPREHENSIVE') {
          console.log('📋 Using comprehensive check endpoint');
          apiUrl = `/projects/${this.projectId}/request-comprehensive-upload/`;
          
          // Add comprehensive-specific fields
          requestPayload.requested_role = 'VENDOR_CONTACT';
          
          if (this.subjectContext && this.subjectContext.subject_entity) {
            requestPayload.subject_entity_id = this.subjectContext.subject_entity;
            
            // Use updated comprehensive check logic with project settings
            console.log('🎯 Using project-configured comprehensive check definition');
          } else {
            throw new Error('Comprehensive check requires a specific subject context.');
          }
          
        } else if (this.analysisType) {
          console.log('⚙️ Using workflow-based endpoint');
          apiUrl = `/projects/${this.projectId}/request-upload/`;
          requestPayload.workflow_id = this.analysisType;
        } else {
          throw new Error('Please select an analysis type or enable skip processing.');
        }
        
        console.log('📡 API Request:', { url: apiUrl, payload: requestPayload });
        
        const response = await api.post(apiUrl, requestPayload);
        console.log('✅ API Response:', response.data);
        
        if (response.data?.status === 'success' || response.data?.message) {
          this.success = true;
          this.$emit('request-sent', { 
            success: true, 
            message: response.data.message || 'Upload request sent successfully!' 
          });
          
          // Clear form on success
          this.resetForm();
        } else {
          throw new Error(response.data?.error || 'Unexpected response format');
        }

      } catch (error) {
        console.error('❌ Upload request failed:', error);
        const errorMessage = this.getErrorMessage(error);
        this.error = errorMessage;
        this.$emit('request-sent', { success: false, message: errorMessage });
      } finally {
        this.loading = false;
      }
    },
    
    // ── CC Helpers ───────────────────────────────────────────────────────────

    addAdditionalCc() {
      const addr = this.additionalCcInput.trim();
      if (!addr) return;
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(addr)) {
        this.ccInputError = `"${addr}" is not a valid email address.`;
        return;
      }
      const lower = addr.toLowerCase();
      if (
        this.additionalCcList.some(e => e.toLowerCase() === lower) ||
        this.recipients.some(r => r.email.toLowerCase() === lower)
      ) {
        this.ccInputError = `${addr} is already in the list.`;
        return;
      }
      this.ccInputError = null;
      this.additionalCcList.push(addr);
      this.additionalCcInput = '';
    },

    removeAdditionalCc(idx) {
      this.additionalCcList.splice(idx, 1);
    },

    // ── Notification Config (READ-ONLY here; editor lives in
    //    ProjectSettings.vue per PR B of the May-2026 COI audit) ──────

    async loadNotifConfig() {
      try {
        const response = await api.get(`/projects/${this.projectId}/upload-request-config/`);
        if (response.data) {
          this.notifConfig = {
            certificate_holder_name: response.data.certificate_holder_name || '',
            certificate_holder_address: response.data.certificate_holder_address || '',
            contact_name: response.data.contact_name || '',
            contact_title: response.data.contact_title || '',
            contact_email: response.data.contact_email || '',
            special_instructions: response.data.special_instructions || '',
          };
          this.notifConfigSaved = !!(
            response.data.certificate_holder_name || response.data.contact_name
          );
        }
      } catch {
        // Non-fatal — send still works without config
      }
    },

    resetForm() {
      this.message = '';
      this.analysisType = '';
      this.skipProcessing = false;
      this.error = null;
      this.success = false;
      this.showContactSuggestion = false;
      this.newEmailInput = '';
      this.recipientError = null;
      this.ccSubcontractor = false;
      this.additionalCcList = [];
      this.additionalCcInput = '';
      this.ccInputError = null;
      this.clearAIDraft();
      // Keep recipients, contacts, and notif config for convenience
    }
  }
}
</script>

<style scoped>
.upload-request-form {
  padding: 8px 4px 20px;
  background: white;
  border-radius: 12px;
  max-width: 100%;
}

.request-purpose {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  align-items: start;
  margin-bottom: 1.25rem;
  padding: 0 4px;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.purpose-body h3 {
  margin: 0 0 0.3rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.purpose-body p {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.45;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.form-control:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.8;
}

.form-help {
  font-size: 13px;
  line-height: 1.4;
}

/* ── Recipients ─────────────────────────────────────────────────────── */

.recipients-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.recipients-header label {
  margin-bottom: 0;
}

.recipients-count {
  font-size: 12px;
  font-weight: 600;
  color: #adb5bd;
  background: #f1f3f5;
  border-radius: 12px;
  padding: 2px 10px;
  transition: all 0.2s ease;
}

.recipients-count.has-recipients {
  background: #e8f5e9;
  color: #2e7d32;
}

.contacts-loading {
  padding: 10px 0;
  font-size: 13px;
  color: #6c757d;
}

.available-contacts {
  background: #f8f9ff;
  border: 1px solid #e0e4ff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}

.available-contacts-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #667eea;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.contact-chips-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.contact-chip-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  width: 100%;
}

.contact-chip-btn:hover {
  border-color: #667eea;
  background: #f0f4ff;
}

.contact-chip-btn.is-added {
  border-color: #28a745;
  background: #f0fff4;
}

.chip-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chip-avatar-sm {
  width: 26px;
  height: 26px;
  font-size: 10px;
}

.chip-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chip-name {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-role {
  font-size: 11px;
  color: #6c757d;
}

.chip-action {
  font-size: 13px;
  color: #adb5bd;
  flex-shrink: 0;
}

.contact-chip-btn.is-added .chip-action {
  color: #28a745;
}

.no-contacts-hint {
  font-size: 12px;
  color: #6c757d;
  padding: 8px;
  margin-bottom: 10px;
}

/* Selected recipients */
.selected-recipients {
  margin-bottom: 12px;
}

.selected-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #28a745;
  margin-bottom: 6px;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 20px;
  padding: 4px 8px 4px 5px;
  font-size: 12px;
  max-width: 100%;
}

.selected-chip-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
}

.selected-name {
  font-weight: 600;
  color: #1b5e20;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.selected-email {
  font-size: 10px;
  color: #388e3c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.chip-remove {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 0 2px;
  font-size: 10px;
  line-height: 1;
  flex-shrink: 0;
}

.chip-remove:hover {
  color: #dc3545;
}

/* Add email row */
.add-email-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.add-email-row .form-control {
  flex: 1;
}

.btn-add-email {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.btn-add-email:hover:not(:disabled) {
  background: #5a6fd6;
}

.btn-add-email:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.recipient-error {
  color: #dc3545;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.success-message {
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
  border-radius: 8px;
  border: 1px solid #c3e6cb;
  display: flex;
  align-items: center;
}

.error-message {
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #f8d7da, #f5c2c7);
  color: #721c24;
  border-radius: 8px;
  border: 1px solid #f5c2c7;
  display: flex;
  align-items: center;
}

.contact-suggestion {
  border-color: #b6d7ff !important;
}

.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  font-size: 13px;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-outline-primary {
  border-color: #667eea;
  color: #667eea;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: white;
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background: #6c757d;
  color: white;
}

.btn-outline-success {
  border-color: #28a745;
  color: #28a745;
}

.btn-outline-success:hover {
  background: #28a745;
  color: white;
}

.btn-outline-danger {
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: white;
}

.d-flex {
  display: flex;
}

.gap-2 {
  gap: 8px;
}

.mt-1 { margin-top: 4px; }
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 12px; }
.mb-2 { margin-bottom: 8px; }

.text-success { color: #28a745; }
.text-info { color: #17a2b8; }
.text-muted { color: #6c757d; }

.small { font-size: 13px; }

.alert {
  padding: 10px 15px;
  border-radius: 6px;
  border: 1px solid;
}

.alert-warning {
  background: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.alert-danger {
  background: #f8d7da;
  border-color: #f5c2c7;
  color: #721c24;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.bg-info {
  background-color: #d1ecf1 !important;
}

.bg-opacity-10 {
  --bs-bg-opacity: 0.1;
  background-color: rgba(var(--bs-info-rgb), var(--bs-bg-opacity)) !important;
}

/* Skip Processing Section */
.skip-processing-group {
  margin-top: 20px;
  padding: 15px;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fcd34d;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.skip-checkbox {
  color: #92400e;
}

.skip-checkbox span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.skip-checkbox i {
  color: #d97706;
}

.skip-info {
  margin-top: 12px;
  padding: 12px;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 13px;
  color: #78350f;
}

.skip-info > i {
  color: #d97706;
  margin-right: 6px;
}

.skip-info ul {
  margin: 8px 0 0 20px;
  padding: 0;
}

.skip-info li {
  margin: 4px 0;
  color: #92400e;
}

.text-warning {
  color: #d97706 !important;
}

/* CC Section */
.cc-section {
  margin-top: 20px;
  padding: 14px 16px;
  background: #f8f9ff;
  border: 1px solid #e0e4ff;
  border-radius: 10px;
}

.cc-section-title {
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
  font-size: 13px;
  color: #2c3e50;
  margin-bottom: 12px;
}

.cc-section-title i {
  color: #667eea;
}

.cc-option {
  margin-bottom: 12px;
}

.cc-checkbox {
  color: #374151;
  font-size: 13px;
}

.cc-checkbox span {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.cc-checkbox i {
  color: #667eea;
}

.cc-email-hint {
  font-size: 12px;
  color: #6c757d;
  font-weight: normal;
}

.cc-help {
  font-size: 12px;
  color: #6c757d;
  margin: 4px 0 0 26px;
  line-height: 1.5;
}

.cc-additional label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
}

.cc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #e8eaff;
  border: 1px solid #c0c8ff;
  border-radius: 14px;
  padding: 3px 8px;
  font-size: 12px;
  color: #3730a3;
}

.cc-chip-remove {
  background: none;
  border: none;
  color: #8b8fcc;
  cursor: pointer;
  padding: 0;
  font-size: 10px;
  line-height: 1;
}

.cc-chip-remove:hover {
  color: #dc3545;
}

/* Notification Config Section (read-only mirror per PR B) */
.notif-config-section {
  margin-top: 20px;
  border: 1px solid #e0e4ff;
  border-radius: 10px;
  overflow: hidden;
}

.notif-config-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9ff;
  user-select: none;
}

.notif-config-header--readonly {
  cursor: default;
}

.notif-edit-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #667eea;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.15s ease;
}

.notif-edit-link:hover {
  background: #eef0ff;
  text-decoration: none;
}

.config-saved-badge--missing {
  background: #fef3c7 !important;
  color: #92400e !important;
}

.notif-config-body--readonly {
  background: white;
  padding: 12px 16px;
  border-top: 1px solid #e0e4ff;
}

.notif-edit-inline {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  margin-left: 4px;
}

.notif-edit-inline:hover {
  text-decoration: underline;
}

.notif-readonly-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notif-readonly-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  font-size: 13px;
  line-height: 1.5;
}

.notif-readonly-label {
  font-weight: 600;
  color: #4a5568;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-top: 2px;
}

.notif-readonly-value {
  color: #2d3748;
}

.notif-readonly-sub {
  white-space: pre-line;
  color: #4a5568;
  margin-top: 2px;
}

.notif-readonly-instructions {
  white-space: pre-line;
  font-size: 12px;
  color: #4a5568;
}

@media (max-width: 540px) {
  .notif-readonly-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

.notif-config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
}

.notif-config-title i:first-child {
  color: #667eea;
}

.toggle-icon {
  color: #667eea;
  font-size: 12px;
}

.config-saved-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
}

.notif-config-body {
  padding: 16px;
  border-top: 1px solid #e0e4ff;
  background: white;
}

.notif-config-desc {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 16px;
}

.notif-contact-row {
  display: flex;
  gap: 12px;
}

.notif-contact-row .form-group {
  flex: 1;
}

.notif-config-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.btn-save-config {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-save-config:hover:not(:disabled) {
  background: #5a6fd6;
}

.btn-save-config:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.config-save-msg {
  font-size: 12px;
}

.config-save-msg.success {
  color: #2e7d32;
}

.config-save-msg.error {
  color: #c62828;
}

/* AI Email Drafting */
.ai-draft-section {
  margin-top: 20px;
}

.ai-draft-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ai-draft-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.ai-draft-title i {
  color: #764ba2;
}

.btn-ai-draft {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-ai-draft:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35);
}

.btn-ai-draft:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.ai-options-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.ai-option-group {
  flex: 1;
}

.ai-option-group label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.form-control-sm {
  padding: 6px 10px;
  font-size: 13px;
}

.ai-preview-block {
  border: 2px solid #764ba2;
  border-radius: 10px;
  padding: 14px;
  background: linear-gradient(135deg, #f8f4ff, #f0eaff);
}

.ai-preview-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 12px;
}

.ai-body-textarea {
  font-size: 13px;
  line-height: 1.6;
  min-height: 160px;
  border-color: #c4a8e0;
}

.ai-body-textarea:focus {
  border-color: #764ba2;
  box-shadow: 0 0 0 0.2rem rgba(118, 75, 162, 0.2);
}

.fw-semibold {
  font-weight: 600;
}

.mb-2 {
  margin-bottom: 8px;
}
</style> 